import { useCallback, useEffect, useState } from 'react';
import { FROM_CACHE_HEADER, CACHED_AT_HEADER } from '../services/connectionStatus';

/**
 * Whether a polling club TV screen is showing live standings.
 *
 * TV screens say they are offline themselves, quietly, rather than wearing the
 * app's connection banner. The club screen (ScorekeepingTv) set the pattern:
 * swap the live marker for "Standings as of 11:18". This gives the other TV
 * screens the same rule:
 *
 * - Generous about blips. A failed refresh changes nothing until nothing live
 *   has arrived for OFFLINE_AFTER_MS.
 * - Never "live" on the strength of a saved copy. When the server is down the
 *   service worker answers with what it kept, which is worth showing but is
 *   not live, so a screen whose only data came from there is stale at once.
 *
 * `asOf` is when the data on screen was current: the time of the last live
 * answer, or when the saved copy was kept.
 */
export const OFFLINE_AFTER_MS = 90000;

function isSaved(response) {
  return response?.headers?.[FROM_CACHE_HEADER] === '1';
}

/**
 * `initialAsOf` is for a screen that starts from a snapshot of its last live
 * data (utils/tvSnapshot.js): stale from the outset, dated when it was kept.
 */
export function useTvFreshness(initialAsOf = null) {
  const [lastLiveAt, setLastLiveAt] = useState(null);
  const [asOf, setAsOf] = useState(initialAsOf);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(timer);
  }, []);

  /**
   * Pass every axios response behind one refresh of the screen. Returns true
   * when all of it was live, which is when the screen should keep a snapshot.
   */
  const noteResponses = useCallback((responses) => {
    const saved = responses.filter(isSaved);
    if (saved.length === 0) {
      const at = Date.now();
      setLastLiveAt(at);
      setAsOf(at);
      setNow(at);
      return true;
    }
    // Some parts came from saved copies, so the screen is only as fresh as the
    // oldest of them.
    const keptAt = saved
      .map((response) => Date.parse(response.headers[CACHED_AT_HEADER]))
      .filter((time) => !Number.isNaN(time));
    if (keptAt.length > 0) {
      setAsOf((current) => Math.min(Math.min(...keptAt), current ?? Infinity));
    }
    return false;
  }, []);

  const stale = lastLiveAt === null || now - lastLiveAt > OFFLINE_AFTER_MS;
  return { stale, asOf, noteResponses };
}

/** What a TV screen says instead of an error while the server is unreachable. */
export function tvLoadErrorMessage(error, fallback) {
  const status = error?.response?.status;
  // A 502-504 carrying the API's own message is the server answering, not the proxy.
  const fromTheApi = Boolean(error?.response?.data?.error);
  if (!error?.response || ((status === 502 || status === 503 || status === 504) && !fromTheApi)) {
    return "Can't reach the club server. Trying again...";
  }
  return error.response.data?.error || fallback;
}
